// ===============================================
// File: src/contexts/TasksContext.jsx
// Purpose: Unified persistence for Daily To-Do tasks
// Storage: /Mudo/tasks/daily.json (via StorageContext)
// - Auto-migrates from old localStorage "mudoDailyTodos" once
// - Rehydrates on boot and after "mudo:vault-restored" / "mudo:files-restored"
// ===============================================

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useStorage } from "@/contexts/StorageContext";

const FILE_PATH = "tasks/daily.json";
const LEGACY_LS_KEY = "mudoDailyTodos";

const TasksContext = createContext(null);

export function useTasks() {
  const ctx = useContext(TasksContext);
  if (!ctx) throw new Error("useTasks must be used within <TasksProvider>");
  return ctx;
}

function uid() {
  return (
    globalThis.crypto?.randomUUID?.() ||
    `todo_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
  );
}

function coerceList(v) {
  return Array.isArray(v) ? v : [];
}

function sameDay(a, b) {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}

export function TasksProvider({ children }) {
  const { loadJSON, saveJSON } = useStorage();

  const [items, setItems] = useState([]);
  const loadedRef = useRef(false);

  // ---- load (with one-time legacy migration) ----
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const disk = await loadJSON(FILE_PATH, []);
        let seed = coerceList(disk);

        // One-time migration from legacy localStorage
        try {
          const raw = localStorage.getItem(LEGACY_LS_KEY);
          if (raw) {
            const legacy = coerceList(JSON.parse(raw));
            if (legacy.length) {
              // merge without duplicating by (title+createdAt)
              const sig = (t) =>
                `${t.title}::${new Date(t.createdAt || 0)
                  .toISOString()
                  .slice(0, 10)}`;
              const seen = new Set(seed.map(sig));
              const merged = [
                ...seed,
                ...legacy.filter((t) => !seen.has(sig(t))),
              ];
              seed = merged;
            }
            // keep legacy for downgrade safety
          }
        } catch {}

        if (alive) setItems(seed);
      } catch {
        if (alive) setItems([]);
      } finally {
        loadedRef.current = true;
      }
    })();

    // refresh after Receive (vault/files)
    const onRestored = async () => {
      try {
        const disk = await loadJSON(FILE_PATH, []);
        if (alive) setItems(coerceList(disk));
      } catch {}
    };
    
    const onDisconnect = () => {
      // CRITICAL: Clear all data when workspace disconnected
      if (alive) {
        setItems([]);
        loadedRef.current = false;
      }
    };
    
    window.addEventListener("mudo:vault-restored", onRestored);
    window.addEventListener("mudo:files-restored", onRestored);
    window.addEventListener("mudo:workspace-disconnected", onDisconnect);

    return () => {
      alive = false;
      window.removeEventListener("mudo:vault-restored", onRestored);
      window.removeEventListener("mudo:files-restored", onRestored);
      window.removeEventListener("mudo:workspace-disconnected", onDisconnect);
    };
  }, [loadJSON]);

  // ---- save on change ----
  useEffect(() => {
    if (!loadedRef.current) return;
    (async () => {
      try {
        await saveJSON(FILE_PATH, items);
      } catch {}
    })();
  }, [items, saveJSON]);

  // ---- CRUD ----
  const add = useCallback((title) => {
    const t = String(title || "").trim();
    if (!t) return;
    setItems((prev) => [
      { id: uid(), title: t, done: false, createdAt: Date.now() },
      ...(prev || []),
    ]);
  }, []);

  const toggle = useCallback((id) => {
    setItems((prev) =>
      (prev || []).map((it) =>
        String(it.id) === String(id) ? { ...it, done: !it.done } : it
      )
    );
  }, []);

  const remove = useCallback((id) => {
    setItems((prev) => (prev || []).filter((it) => String(it.id) !== String(id)));
  }, []);

  const markAddedToPlanner = useCallback((id) => {
    setItems((prev) =>
      (prev || []).map((it) =>
        String(it.id) === String(id) ? { ...it, addedToPlanner: true } : it
      )
    );
  }, []);

  // helpers
  const todayList = useMemo(() => {
    const today = new Date();
    return (items || []).filter((t) => sameDay(t.createdAt || 0, today));
  }, [items]);

  const ctx = useMemo(
    () => ({
      items,
      setItems,

      // filtered convenience
      todayList,

      // crud
      add,
      toggle,
      remove,
      markAddedToPlanner,
    }),
    [items, todayList, add, toggle, remove, markAddedToPlanner]
  );

  return <TasksContext.Provider value={ctx}>{children}</TasksContext.Provider>;
}
